import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { useState } from 'react'
import { ChatService } from '../../lib/api'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import {
	GetChatMessagesDataResponse,
	UpdateChatProps,
} from '../../lib/api/types/chat.interfaces'

export const useChatMessages = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetChatMessagesDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetChatMessagesDataResponse>({
		queryKey: ['chatMessages', id],
		queryFn: () => ChatService.getChatMessages({ id }),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

export const useCreateChat = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['createChat'],
		mutationFn: ({
			fileId,
			title,
			isTemporary = false,
			invalidate,
		}: {
			fileId: string
			title?: string
			isTemporary?: boolean
			invalidate?: boolean
		}) => ChatService.create({ fileId, title, isTemporary }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: (_, { invalidate = true }) => {
			invalidate &&
				queryClient.invalidateQueries({
					queryKey: ['fileChats'],
				})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const result = await mutation.mutateAsync(...params)
			return result
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateChat = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['chatUpdate'],
		mutationFn: ({ data, id }: UpdateChatProps) =>
			ChatService.update({ data, id }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['fileChats'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteChat = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['chatDelete'],
		mutationFn: ({ id }: DefaultGetParamsProps) => ChatService.delete({ id }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['fileChats'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useSendUserMessage = () => {
	const queryClient = useQueryClient()

	const [currentSendingIds, setCurrentSendingIds] = useState<string[]>([])

	const mutation = useMutation({
		mutationKey: ['sendUserMessage'],
		mutationFn: ({
			id,
			text,
			senderId,
		}: {
			id: string
			text: string
			senderId: string
		}) => ChatService.sendMessage({ id, text }),
		onError: (
			error,
			variables,
			context:
				| { tempId: string; queryKey: any; previousMessages: any }
				| undefined,
		) => {
			console.error(error)
			if (!context) return
			const { tempId, queryKey } = context
			queryClient.setQueryData(
				queryKey,
				(chatData: GetChatMessagesDataResponse | undefined) => {
					if (!chatData) return chatData
					return {
						...chatData,
						data: {
							...chatData.data,
							messages: chatData.data.messages.map((msg) =>
								msg.id === tempId ? { ...msg, is_error: true } : msg,
							),
						},
					}
				},
			)
		},

		onMutate: async (sendData) => {
			setCurrentSendingIds((prev) => [...prev, sendData.id])
			const queryKey = ['chatMessages', sendData.id]

			const previousMessages =
				queryClient.getQueryData<GetChatMessagesDataResponse>(queryKey)

			const tempId = `temp-${Date.now()}`

			queryClient.setQueryData(
				queryKey,
				(chatData: GetChatMessagesDataResponse | undefined) => {
					const newMessage = {
						id: tempId,
						text: sendData.text,
						createdAt: new Date().toISOString(),
						senderId: sendData?.senderId,
						is_error: false,
					}

					if (!chatData) {
						return {
							data: {
								messages: [newMessage],
							},
						}
					}

					return {
						...chatData,
						data: {
							...chatData.data,
							messages: [...chatData.data.messages, newMessage],
						},
					}
				},
			)

			return { previousMessages, queryKey, tempId }
		},

		onSettled: async (
			response,
			error,
			variables,
			context:
				| { tempId: string; queryKey: any; previousMessages: any }
				| undefined,
		) => {
			if (!context) return
			const { tempId, queryKey } = context
			const { id } = variables

			setCurrentSendingIds((prev) => prev.filter((prevId) => prevId !== id))

			if (!response) return

			await queryClient.setQueryData(
				queryKey,
				(chatData: GetChatMessagesDataResponse | undefined) => {
					if (!chatData) return response

					const messagesWithoutTemp = chatData.data.messages.filter(
						(msg) => msg.id !== tempId,
					)

					const seenIds = new Set()
					const messages = [...messagesWithoutTemp, ...response.data].filter(
						(item) => {
							if (seenIds.has(item.id) || !item?.id) {
								return false
							}
							seenIds.add(item.id)
							return true
						},
					)

					return {
						...chatData,
						data: {
							...chatData.data,
							messages,
						},
					}
				},
			)
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			const result = await mutation.mutateAsync(...params)
			return result
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync, currentSendingIds }
}
